import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as D from "@radix-ui/react-dialog";
import {
  FileText,
  Search,
  User,
  Wrench,
  Package,
  Repeat,
  Building2,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { useAppStore } from "@/stores/app.store";

type Result = { id: string; label: string; sub?: string };
type Group = {
  key: string;
  title: string;
  icon: typeof User;
  results: Result[];
  go: (id: string) => string;
};

/**
 * Globale Schnellsuche (Cmd/Strg+K) — springt direkt zu einem Kunden, Beleg, Objekt oder Artikel,
 * ohne vorher auf die richtige Liste wechseln zu müssen (AUDIT-2026-10-01-UX.md, Punkt 3.1).
 * Auftrag/Objekt/Artikel haben keine eigene Route (werden als Dialog auf ihrer Listenseite
 * geöffnet) — dafür springt die Suche auf die Liste mit `?open=<id>`, die Liste öffnet den
 * passenden Dialog dann selbst (siehe Orders/Properties/Articles `index.tsx`).
 */
export function QuickSwitcher() {
  const open = useAppStore((s) => s.quickSwitcherOpen);
  const setOpen = useAppStore((s) => s.setQuickSwitcherOpen);
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(!open);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, setOpen]);

  useEffect(() => {
    if (!open) {
      setQ("");
      setDebounced("");
      setActive(0);
    }
  }, [open]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 200);
    return () => clearTimeout(t);
  }, [q]);

  const search = trpc.search.global.useQuery(
    { q: debounced },
    { enabled: debounced.length >= 2 },
  );
  const d = search.data;

  const groups: Group[] = useMemo(
    () => [
      {
        key: "customers",
        title: "Kunden",
        icon: User,
        results: d?.customers ?? [],
        go: (id) => `/kunden/${id}`,
      },
      {
        key: "invoices",
        title: "Rechnungen",
        icon: FileText,
        results: d?.invoices ?? [],
        go: (id) => `/rechnungen/${id}`,
      },
      {
        key: "quotes",
        title: "Angebote",
        icon: FileText,
        results: d?.quotes ?? [],
        go: (id) => `/angebote/${id}`,
      },
      {
        key: "orders",
        title: "Aufträge",
        icon: Wrench,
        results: d?.orders ?? [],
        go: (id) => `/auftraege?open=${id}`,
      },
      {
        key: "contracts",
        title: "Verträge",
        icon: Repeat,
        results: d?.contracts ?? [],
        go: (id) => `/vertraege/${id}`,
      },
      {
        key: "properties",
        title: "Objekte",
        icon: Building2,
        results: d?.properties ?? [],
        go: (id) => `/objekte?open=${id}`,
      },
      {
        key: "articles",
        title: "Artikel",
        icon: Package,
        results: d?.articles ?? [],
        go: (id) => `/artikel?open=${id}`,
      },
    ],
    [d],
  );

  const flat = useMemo(
    () =>
      groups.flatMap((g) => g.results.map((r) => ({ group: g, result: r }))),
    [groups],
  );
  const hasResults = flat.length > 0;

  const goTo = (group: Group, id: string) => {
    setOpen(false);
    navigate(group.go(id));
  };

  return (
    <D.Root open={open} onOpenChange={setOpen}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 bg-black/60 animate-fade-in z-40" />
        <D.Content
          className="fixed left-1/2 top-[14vh] -translate-x-1/2 z-50 w-[calc(100vw-2rem)] max-w-lg card shadow-modal p-0 overflow-hidden animate-slide-up"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            inputRef.current?.focus();
          }}
        >
          <D.Title className="sr-only">Schnellsuche</D.Title>
          <div className="flex items-center gap-2 px-4 py-3 border-b border-border-subtle">
            <Search className="w-4 h-4 text-text-tertiary shrink-0" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setActive(0);
              }}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setActive((i) => Math.min(i + 1, flat.length - 1));
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setActive((i) => Math.max(i - 1, 0));
                } else if (e.key === "Enter") {
                  e.preventDefault();
                  const hit = flat[active];
                  if (hit) goTo(hit.group, hit.result.id);
                }
              }}
              placeholder="Kunde, Rechnung, Angebot, Objekt, Artikel …"
              className="flex-1 bg-transparent outline-none text-sm text-text-primary placeholder:text-text-tertiary"
            />
            <kbd className="text-2xs text-text-tertiary border border-border-subtle rounded px-1.5 py-0.5">
              Esc
            </kbd>
          </div>
          <div className="max-h-[50vh] overflow-y-auto py-2">
            {debounced.length < 2 ? (
              <p className="px-4 py-6 text-sm text-text-tertiary text-center">
                Mindestens 2 Zeichen eingeben …
              </p>
            ) : search.isLoading ? (
              <p className="px-4 py-6 text-sm text-text-tertiary text-center">
                Sucht …
              </p>
            ) : !hasResults ? (
              <p className="px-4 py-6 text-sm text-text-tertiary text-center">
                Keine Treffer.
              </p>
            ) : (
              groups
                .filter((g) => g.results.length)
                .map((g) => (
                  <div key={g.key} className="mb-1">
                    <div className="px-4 py-1 text-2xs font-medium uppercase tracking-wide text-text-tertiary">
                      {g.title}
                    </div>
                    {g.results.map((r) => {
                      const idx = flat.findIndex(
                        (f) => f.group.key === g.key && f.result.id === r.id,
                      );
                      const Icon = g.icon;
                      return (
                        <button
                          key={r.id}
                          onMouseEnter={() => setActive(idx)}
                          onClick={() => goTo(g, r.id)}
                          className={cn(
                            "w-full flex items-center gap-2.5 px-4 py-2 text-left text-sm",
                            idx === active
                              ? "bg-bg-active text-text-primary"
                              : "text-text-secondary",
                          )}
                        >
                          <Icon className="w-3.5 h-3.5 text-text-tertiary shrink-0" />
                          <span className="flex-1 truncate">{r.label}</span>
                          {r.sub && (
                            <span className="text-2xs text-text-tertiary truncate max-w-[40%]">
                              {r.sub}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))
            )}
          </div>
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}
